const API_BASE = '/api/v1';

// Check if user is authenticated via SSO session (X-User-ID is the dev fallback)
export async function checkAuth() {
  const userId = getCurrentUserId();
  const response = await fetch('/auth/me', {
    headers: { ...(userId && { 'X-User-ID': userId }) },
  });
  const data = await response.json();
  return data;
}

// Redirect to Microsoft SSO login
export function loginWithMicrosoft() {
  window.location.href = '/auth/login';
}

// Logout via SSO
export function logout() {
  window.location.href = '/auth/logout';
}

// Keep setCurrentUser/getCurrentUserId for dev fallback (X-User-ID header)
let currentUserId = null;

export function setCurrentUser(userId) {
  currentUserId = userId;
  if (userId) {
    localStorage.setItem('k2_kudos_user_id', userId);
  } else {
    localStorage.removeItem('k2_kudos_user_id');
  }
}

export function getCurrentUserId() {
  if (!currentUserId) {
    currentUserId = localStorage.getItem('k2_kudos_user_id');
  }
  return currentUserId;
}

async function request(endpoint, options = {}) {
  const userId = getCurrentUserId();

  const config = {
    ...options,
    credentials: 'include', // Send session cookie
    headers: {
      'Content-Type': 'application/json',
      ...(userId && { 'X-User-ID': userId }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_BASE}${endpoint}`, config);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

// Users
export const getMe = () => request('/users/me');
export const getUsers = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/users${query ? `?${query}` : ''}`);
};

export const getLeaderboard = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/users/leaderboard${query ? `?${query}` : ''}`);
};
export const getUser = (id) => request(`/users/${id}`);
export const getUserBadges = (id) => request(`/users/${id}/badges`);
export const getUserBadgeProgress = (id) => request(`/users/${id}/badge-progress`);
export const getTeamMembers = () => request('/users/team/members');

// Tags
export const getTags = () => request('/tags');
export const getValues = () => request('/tags/values');
export const getFundamentals = () => request('/tags/fundamentals');

// Recognitions
export const getRecognitions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/recognitions${query ? `?${query}` : ''}`);
};
export const getRecognition = (id) => request(`/recognitions/${id}`);
export const createRecognition = (data) => request('/recognitions', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const editRecognition = (id, { message, receiver_id }) => request(`/recognitions/${id}`, {
  method: 'PUT',
  body: JSON.stringify({ message, receiver_id }),
});
export const deleteRecognitionByGiver = (id) => request(`/recognitions/${id}`, { method: 'DELETE' });
export const likeRecognition = (id) => request(`/recognitions/${id}/like`, { method: 'POST' });
export const unlikeRecognition = (id) => request(`/recognitions/${id}/like`, { method: 'DELETE' });
export const getComments = (id) => request(`/recognitions/${id}/comments`);
export const addComment = (id, content, gif_url) => request(`/recognitions/${id}/comments`, {
  method: 'POST',
  body: JSON.stringify({ content, gif_url }),
});
export const deleteComment = (recognitionId, commentId) => request(`/recognitions/${recognitionId}/comments/${commentId}`, {
  method: 'DELETE',
});

// Admin - Stats & Reports
export const getAdminStats = () => request('/admin/stats');
export const getAdminRecognitions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/recognitions${query ? `?${query}` : ''}`);
};
export const getAdminLeaderboard = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/leaderboard${query ? `?${query}` : ''}`);
};
export const getDepartments = () => request('/admin/departments');
export const getLocations = () => request('/admin/locations');
export const exportRecognitions = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return `${API_BASE}/admin/export${query ? `?${query}` : ''}`;
};

// Admin - Tags Management
export const getAdminTags = () => request('/admin/tags');
export const createTag = (data) => request('/admin/tags', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateTag = (id, data) => request(`/admin/tags/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteTag = (id) => request(`/admin/tags/${id}`, { method: 'DELETE' });

// Admin - Badges Management
export const getAdminBadges = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/badges${query ? `?${query}` : ''}`);
};
export const awardBadge = (data) => request('/admin/badges', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const deleteBadge = (id) => request(`/admin/badges/${id}`, { method: 'DELETE' });

// Admin - Recognition Management
export const deleteRecognition = (id) => request(`/admin/recognitions/${id}`, { method: 'DELETE' });
export const updateRecognition = (id, data) => request(`/admin/recognitions/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});

// Manager Dashboard
export const getTeamDashboard = (managerId) => request(`/admin/team/${managerId}`);

// Admin - User Management
export const getAdminUsers = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/users${query ? `?${query}` : ''}`);
};
export const createUser = (data) => request('/admin/users', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateUser = (id, data) => request(`/admin/users/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteUser = (id) => request(`/admin/users/${id}`, { method: 'DELETE' });

// Reports
export const getExecutiveSummary = () => request('/reports/executive-summary');
export const getParticipationReport = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/participation${query ? `?${query}` : ''}`);
};
export const getInactiveUsers = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/inactive-users${query ? `?${query}` : ''}`);
};
export const getTrendsReport = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/trends${query ? `?${query}` : ''}`);
};
export const getRecognitionFlow = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/recognition-flow${query ? `?${query}` : ''}`);
};
export const getManagerEngagement = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/manager-engagement${query ? `?${query}` : ''}`);
};
export const getValuesBreakdown = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/values-breakdown${query ? `?${query}` : ''}`);
};
export const getFundamentalsBreakdown = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request(`/reports/fundamentals-breakdown${query ? `?${query}` : ''}`);
};

// Translation
export const translateText = (text, targetLang) => request('/translate', {
  method: 'POST',
  body: JSON.stringify({ text, targetLang }),
});

// AI Enhance
export const enhanceMessage = (text, recipientName, value, fundamental) => request('/translate/enhance', {
  method: 'POST',
  body: JSON.stringify({ text, recipientName, value, fundamental }),
});
